import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { trackProjectEvents } from '../../../../lib/analyticsEnhanced';
import { FolderSelector } from '../folders/FolderSelector';

interface ProjectEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (title: string, description: string, folderId?: string | null) => Promise<boolean>;
  onMergeClick?: () => void; // 新增
  projectId: string;
  initialTitle: string;
  initialDescription: string;
  initialFolderId?: string | null;
  isCreating?: boolean; // 新增，用于区分创建和编辑模式
}

export const ProjectEditModal: React.FC<ProjectEditModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onMergeClick,
  projectId,
  initialTitle,
  initialDescription,
  initialFolderId,
  isCreating = false
}) => {
  const { t } = useTranslation(['v3/common', 'v3/projects']);

  // 状态
  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState(initialDescription);
  const [folderId, setFolderId] = useState<string | null>(initialFolderId || null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 当初始值变化时更新状态
  useEffect(() => {
    if (isOpen) {
      console.log('ProjectEditModal: useEffect 更新状态，initialFolderId =', initialFolderId);
      setTitle(initialTitle);
      setDescription(initialDescription);
      setFolderId(initialFolderId || null);
      setError(null);
    }
  }, [isOpen, initialTitle, initialDescription, initialFolderId]);

  // 处理保存
  const handleSave = async () => {
    if (!title.trim()) {
      setError(t('v3/projects:projectForm.errors.emptyTitle'));
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      console.log('ProjectEditModal: 保存项目，当前状态 folderId =', folderId);
      console.log('ProjectEditModal: 调用 onSave，参数:', { title, description, folderId });
      const success = await onSave(title, description, folderId);
      if (success) {
        // 记录项目保存事件
        if (isCreating) {
          trackProjectEvents.projectCreated({
            title,
            context: description,
            title_length: title.length,
            context_length: description.length,
            has_context: !!description.trim()
          });
        } else {
          trackProjectEvents.projectEdited(projectId, {
            title_changed: title !== initialTitle,
            description_changed: description !== initialDescription,
            title_length: title.length,
            context_length: description.length
          });
        }
        onClose();
      } else {
        setError(t('v3/common:errors.saveFailed'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('v3/common:errors.saveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  // 处理取消
  const handleCancel = () => {
    onClose();
  };

  // 处理背景点击
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 dark:bg-black/50"
          onClick={handleBackdropClick}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="w-full max-w-md mx-4 bg-white dark:bg-ios-dark-gray6 rounded-ios-lg shadow-ios dark:shadow-ios-dark overflow-hidden"
          >
            {/* 模态框标题 */}
            <div className="px-4 py-3 border-b border-ios-gray5 dark:border-ios-dark-gray4">
              <h2 className="text-ios-title font-semibold text-center">
                {isCreating ? t('v3/projects:actions.createProject') : t('v3/projects:actions.editProject')}
              </h2>
            </div>

            {/* 模态框内容 */}
            <div className="p-4">
              {/* 错误提示 */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-ios-md text-ios-subhead">
                  {error}
                </div>
              )}

              {/* 项目标题 */}
              <div className="mb-4">
                <label className="block text-ios-subhead font-medium text-ios-gray dark:text-ios-dark-gray mb-1">
                  {t('v3/projects:projectForm.title')}
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full h-10 px-3 bg-ios-gray5 dark:bg-ios-dark-gray4 rounded-ios-md text-ios-body focus:outline-none"
                  placeholder={t('v3/projects:projectForm.titlePlaceholder')}
                />
              </div>

              {/* 文件夹选择 */}
              <div className="mb-4">
                <label className="block text-ios-subhead font-medium text-ios-gray dark:text-ios-dark-gray mb-1">
                  {t('v3/projects:projectForm.folder', '文件夹')}
                </label>
                <FolderSelector
                  value={folderId}
                  onChange={setFolderId}
                />
              </div>

              {/* 项目描述 */}
              <div className="mb-4">
                <label className="block text-ios-subhead font-medium text-ios-gray dark:text-ios-dark-gray mb-1">
                  {t('v3/projects:projectForm.description')}
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full h-32 p-3 bg-ios-gray5 dark:bg-ios-dark-gray4 rounded-ios-md text-ios-body focus:outline-none resize-none"
                  placeholder={t('v3/projects:projectForm.descriptionPlaceholder')}
                />
              </div>
            </div>

            {/* 模态框按钮 */}
            <div className="flex border-t border-ios-gray5 dark:border-ios-dark-gray4">
              <button
                onClick={handleCancel}
                className="flex-1 py-3 text-ios-body text-ios-gray dark:text-ios-dark-gray font-medium border-r border-ios-gray5 dark:border-ios-dark-gray4"
              >
                {t('v3/common:actions.cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex-1 py-3 text-ios-body text-ios-blue dark:text-ios-dark-blue font-medium"
              >
                {isSaving ? (
                  <span className="inline-block w-5 h-5 border-2 border-ios-blue/30 dark:border-ios-dark-blue/30 border-t-ios-blue dark:border-t-ios-dark-blue rounded-full animate-spin mx-auto"></span>
                ) : (
                  isCreating ? t('v3/common:actions.create') : t('v3/common:actions.save')
                )}
              </button>
            </div>

            {/* 合并项目按钮 */}
            {onMergeClick && (
              <div className="px-4 py-3 border-t border-ios-gray5 dark:border-ios-dark-gray4">
                <button
                  onClick={onMergeClick}
                  className="w-full py-3 text-ios-body text-ios-red dark:text-ios-dark-red font-medium rounded-ios-md bg-ios-gray5 dark:bg-ios-dark-gray4"
                >
                  {t('v3/projects:actions.mergeProject')}
                </button>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
